/**
 * Per-feature record scope (`permissions.record_scopes[featureKey]`).
 *
 * Narrows which rows a member sees for a module that supports it (CRM first).
 * Ordering rule: level first, then scope — a disabled feature never reaches this layer.
 *
 * @see CRM_UI_UX_DESIGN.md §6.7.2
 */

export type RecordScope = 'own' | 'team' | 'all';

/** Features that currently expose a record-scope control in the team dialog. */
export const RECORD_SCOPE_FEATURE_KEYS = ['crm'] as const;

export type RecordScopeFeatureKey = (typeof RECORD_SCOPE_FEATURE_KEYS)[number];

export function isRecordScope(value: unknown): value is RecordScope {
    return value === 'own' || value === 'team' || value === 'all';
}

export function usesRecordScope(featureKey: string): boolean {
    return (RECORD_SCOPE_FEATURE_KEYS as readonly string[]).includes(featureKey);
}

/**
 * Read `record_scopes[featureKey]` from a permissions payload (get-perms or member row).
 * Returns `fallback` when missing / invalid.
 */
export function resolveRecordScope(
    permissions: Record<string, unknown> | null | undefined,
    featureKey: string,
    fallback: RecordScope = 'own'
): RecordScope {
    if (!permissions || typeof permissions !== 'object') return fallback;
    const scopes = permissions['record_scopes'];
    if (!scopes || typeof scopes !== 'object' || Array.isArray(scopes)) return fallback;
    const raw = (scopes as Record<string, unknown>)[featureKey];
    const normalized = String(raw ?? '')
        .trim()
        .toLowerCase();
    return isRecordScope(normalized) ? normalized : fallback;
}

/** Build / merge a `record_scopes` object for a create/update permissions payload. */
export function withRecordScope(
    permissions: Record<string, unknown>,
    featureKey: string,
    scope: RecordScope
): Record<string, unknown> {
    const existing = permissions['record_scopes'];
    const nextScopes: Record<string, string> =
        existing && typeof existing === 'object' && !Array.isArray(existing)
            ? { ...(existing as Record<string, string>) }
            : {};
    nextScopes[featureKey] = scope;
    return { ...permissions, record_scopes: nextScopes };
}

/**
 * What the actor may do with a record in their scope that someone ELSE owns
 * (`permissions.record_actions[featureKey]`). Their own records are always fully editable.
 * Ordered: `edit` covers reassigning, `delete` also covers editing.
 */
export type RecordAction = 'view' | 'edit' | 'delete';

const RECORD_ACTION_RANK: Record<RecordAction, number> = { view: 0, edit: 1, delete: 2 };

export function isRecordAction(value: unknown): value is RecordAction {
    return value === 'view' || value === 'edit' || value === 'delete';
}

/** True when `granted` covers `needed`. */
export function recordActionAllows(granted: RecordAction, needed: RecordAction): boolean {
    return RECORD_ACTION_RANK[granted] >= RECORD_ACTION_RANK[needed];
}

/**
 * The per-role default when nothing is stored, matching the backend's `normalizeRecordAction`:
 * a manager keeps full edit and delete, a staff rep is view-only on other people's records.
 */
export function defaultRecordAction(role: 'manager' | 'staff'): RecordAction {
    return role === 'staff' ? 'view' : 'delete';
}

/** Read `record_actions[featureKey]` from a permissions payload; `fallback` when missing / invalid. */
export function resolveRecordAction(
    permissions: Record<string, unknown> | null | undefined,
    featureKey: string,
    fallback: RecordAction
): RecordAction {
    if (!permissions || typeof permissions !== 'object') return fallback;
    const actions = permissions['record_actions'];
    if (!actions || typeof actions !== 'object' || Array.isArray(actions)) return fallback;
    const raw = String((actions as Record<string, unknown>)[featureKey] ?? '')
        .trim()
        .toLowerCase();
    return isRecordAction(raw) ? raw : fallback;
}

/** Build / merge a `record_actions` object for a create/update permissions payload. */
export function withRecordAction(
    permissions: Record<string, unknown>,
    featureKey: string,
    action: RecordAction
): Record<string, unknown> {
    const existing = permissions['record_actions'];
    const nextActions: Record<string, string> =
        existing && typeof existing === 'object' && !Array.isArray(existing)
            ? { ...(existing as Record<string, string>) }
            : {};
    nextActions[featureKey] = action;
    return { ...permissions, record_actions: nextActions };
}
